<template>
  <div class="actualidad-container">
    <!-- Skeleton Loader -->
    <div v-if="loading" class="posts-list">
      <div v-for="i in 10" :key="i" class="skeleton-post">
        <div class="skeleton-image"></div>
        <div class="skeleton-content">
          <div class="skeleton-category"></div>
          <div class="skeleton-title"></div>
          <div class="skeleton-title short"></div>
          <div class="skeleton-date"></div>
        </div>
      </div>
    </div>

    <!-- Lista de posts -->
    <div v-else-if="posts.length > 0" class="posts-list">
      <div 
        v-for="post in posts" 
        :key="post.id"
        class="post-item"
        @click="goToPost(post)"
      >
        <img :src="getImageUrl(post.image)" :alt="post.title" class="post-image">
        <div class="post-content">
          <div class="post-meta">
            <span class="post-category">{{ post.category?.name || 'Noticia' }}</span>
            <span class="post-date">{{ formatDate(post.created_at) }}</span>
          </div>
          <h3 class="post-title">{{ post.title }}</h3>
          <p class="post-excerpt">{{ post.extract }}</p>
        </div>
      </div>
    </div>

    <div v-else class="empty-posts">
      No hay publicaciones disponibles
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../services/api'

const router = useRouter()
const posts = ref([])
const loading = ref(true)

const getImageUrl = (image) => {
  if (!image) return '/noimagen.png'
  if (image.startsWith('http')) return image
  return `https://portal.jlhvision.com/posts/${image}`
}

const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000 / 60 / 60)
  
  if (diff < 1) return 'Hace unos minutos'
  if (diff < 24) return `Hace ${diff} horas`
  if (diff < 48) return 'Ayer'
  return d.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const loadPosts = async () => {
  loading.value = true
  try {
    const response = await api.get('/posts')
    posts.value = response.data.posts.data || []
  } catch (error) {
    console.error('Error cargando posts:', error)
    posts.value = []
  } finally {
    loading.value = false
  }
}

const goToPost = (post) => {
  router.push(`/noticia/${post.slug}`)
}

onMounted(() => {
  loadPosts()
})
</script>

<style scoped>
.actualidad-container {
  width: 100%;
  margin: 15px 0;
}

/* Lista de posts */
.posts-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.post-item {
  display: flex;
  gap: 15px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
  padding: 0;
}

.post-item:active {
  transform: scale(0.99);
}

.post-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  object-position: center;
  flex-shrink: 0;
  border-radius: 12px;
}

.post-content {
  flex: 1;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* contenedor para categoría y fecha con space-between */
.post-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.post-category {
  display: inline-block;
  background: transparent;
  color: #a50000;
  font-size: 11px;
  padding: 0;
  margin: 0;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.5px;
}

.post-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 🔥 NUEVO: extracto */
.post-excerpt {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-date {
  font-size: 11px;
  color: #999;
  margin: 0;
}

/* Skeleton */
.skeleton-post {
  display: flex;
  gap: 15px;
  background: white;
  border-radius: 12px;
  padding: 0;
}

.skeleton-image {
  width: 120px;
  height: 120px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
  flex-shrink: 0;
}

.skeleton-content {
  flex: 1;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-category {
  width: 80px;
  height: 14px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title {
  width: 90%;
  height: 18px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title.short {
  width: 60%;
}

.skeleton-date {
  width: 80px;
  height: 12px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.empty-posts {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 480px) {
  .post-image {
    width: 100px;
    height: 100px;
  }
  
  .post-title {
    font-size: 14px;
  }
  
  .post-excerpt {
    font-size: 12px;
  }
  
  .post-category,
  .post-date {
    font-size: 10px;
  }
}
</style>